<template>
    <div
        class="email-verification-flow"
        :class="[modeClass, { 'dark-mode': darkMode }]"
    >
        <!-- Loading overlay for dark mode -->
        <div v-if="darkMode && isResendingEmail" class="dark-loading-overlay">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ t('general.loading') }}...</p>
        </div>

        <div class="verification-content">
            <h1 class="verification-title">
                {{ t('login.email-verification') }}
            </h1>

            <div class="icon-container">
                <img
                    src="@/assets/icons/email-verify.svg"
                    alt="Email Verification"
                    class="icon"
                />
            </div>

            <p class="verification-description">
                {{ t('login.verification-email-sent-1') }}
                <strong>{{ userEmail }}</strong>
                {{ t('login.verification-email-sent-2') }}
            </p>

            <div class="didnt-receive-section">
                <a
                    v-if="!showResendButton"
                    @click="showResendButton = true"
                    class="didnt-receive-link"
                >
                    {{ t('login.didnt-receive-email') }}
                </a>

                <transition name="fade-slide">
                    <Button
                        v-if="showResendButton"
                        class="shadow-btn resend-button"
                        :label="resendButtonLabel"
                        @click="resendVerificationEmail"
                        :loading="isResendingEmail"
                        :disabled="isResendingEmail || resendCooldown > 0"
                    />
                </transition>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'

import firebase from 'firebase/compat/app'

import * as authUtils from '@/utils/auth.js'
import * as toastUtils from '@/utils/toast.js'

const props = defineProps({
    mode: {
        type: String,
        default: 'page',
        validator: (value) => ['page', 'modal'].includes(value),
    },
    darkMode: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['verified'])

const { t } = useI18n()
const toast = useToast()

const userEmail = ref('')
const isResendingEmail = ref(false)
const resendCooldown = ref(0)
const showResendButton = ref(false)

let cooldownInterval = null
let verificationCheckInterval = null

const modeClass = computed(() =>
    props.mode === 'modal' ? 'mode-modal' : 'mode-page'
)

const resendButtonLabel = computed(() => {
    if (resendCooldown.value > 0) {
        return `${t('login.resend-verification-email')} (${resendCooldown.value}s)`
    }
    return t('login.resend-verification-email')
})

onMounted(async () => {
    const currentUser = firebase.auth().currentUser

    if (!currentUser) {
        emit('verified')
        return
    }

    const usedEmailPassword = currentUser.providerData.some(
        (provider) => provider.providerId === 'password'
    )

    if (!usedEmailPassword || currentUser.emailVerified) {
        emit('verified')
        return
    }

    userEmail.value = currentUser.email

    await checkVerificationSilently()

    verificationCheckInterval = setInterval(async () => {
        await checkVerificationSilently()
    }, 5000)
})

onUnmounted(() => {
    if (cooldownInterval) {
        clearInterval(cooldownInterval)
    }
    if (verificationCheckInterval) {
        clearInterval(verificationCheckInterval)
    }
})

async function checkVerificationSilently() {
    try {
        const result = await authUtils.reloadUserAuthState()

        if (result.success && result.emailVerified) {
            toastUtils.showSuccess(
                toast,
                t('login.email-verified'),
                t('login.email-verified-detail')
            )

            if (cooldownInterval) {
                clearInterval(cooldownInterval)
                cooldownInterval = null
            }
            if (verificationCheckInterval) {
                clearInterval(verificationCheckInterval)
                verificationCheckInterval = null
            }

            emit('verified')
        }
    } catch (error) {
        console.error('Error checking verification silently:', error)
    }
}

function startResendCooldown(seconds) {
    resendCooldown.value = seconds

    if (cooldownInterval) {
        clearInterval(cooldownInterval)
    }

    cooldownInterval = setInterval(() => {
        resendCooldown.value--
        if (resendCooldown.value <= 0) {
            clearInterval(cooldownInterval)
            cooldownInterval = null
        }
    }, 1000)
}

async function resendVerificationEmail() {
    isResendingEmail.value = true
    try {
        const result = await authUtils.sendEmailVerification()

        if (result.success) {
            toastUtils.showSuccess(
                toast,
                t('login.verification-email-resent'),
                t('login.verification-email-resent-detail')
            )
            startResendCooldown(60)
        } else {
            toastUtils.showError(
                toast,
                result.error || t('general.general-error')
            )
        }
    } catch (error) {
        console.error('Error resending verification email:', error)
        toastUtils.showError(toast, t('general.general-error'))
    } finally {
        isResendingEmail.value = false
    }
}
</script>

<style scoped>
.email-verification-flow {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background-color: white;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 40px 30px;
    box-sizing: border-box;
}

.email-verification-flow.mode-page {
    max-width: 400px;
}

.email-verification-flow.mode-modal {
    max-width: 320px;
    padding: 32px 24px;
    box-shadow: none;
}

.verification-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.icon-container {
    margin-bottom: 24px;
}

.icon {
    width: 137px;
    height: 137px;
}

.verification-title {
    font-size: 32px;
    font-weight: 500;
    letter-spacing: -1px;
    margin-bottom: 35px;
    line-height: 38px;
    font-family: 'Basier Circle', sans-serif;
    color: #3d3d3d;
}

.email-verification-flow.mode-modal .verification-title {
    font-size: 26px;
    margin-bottom: 24px;
}

.verification-description {
    font-size: 14px;
    font-weight: 300;
    color: #3d3d3d;
    margin-bottom: 16px;
    line-height: 18px;
    font-family: 'Nunito', sans-serif;
    text-wrap-style: pretty;
}

.didnt-receive-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.didnt-receive-link {
    font-size: 14px;
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    font-family: 'Nunito', sans-serif;
    transition: color 0.2s ease;
    padding: 8px;
}

.didnt-receive-link:hover {
    color: #6e3dea;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.shadow-btn {
    margin-bottom: 12px;
    width: 100%;
    max-width: 320px;
    height: 50px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 5px #00000021;
    font-size: 14px;
    font-weight: 400;
}

.email-verification-flow.mode-modal .shadow-btn {
    max-width: 100%;
}

.resend-button {
    background-color: #f0f0f0;
    color: #333;
    border: none;
}

.resend-button:hover:not(:disabled) {
    background-color: #e0e0e0;
}

.shadow-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.dark-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 3;
    gap: 16px;
    border-radius: inherit;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    color: white;
    font-size: 14px;
    font-family: 'Nunito', sans-serif;
    margin: 0;
}

.email-verification-flow.dark-mode {
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: none;
}

.email-verification-flow.dark-mode .verification-title {
    color: white;
}

.email-verification-flow.dark-mode .verification-description {
    color: rgba(255, 255, 255, 0.75);
}

.email-verification-flow.dark-mode .verification-description strong {
    color: white;
}

.email-verification-flow.dark-mode .didnt-receive-link {
    color: rgba(255, 255, 255, 0.75);
}

.email-verification-flow.dark-mode .didnt-receive-link:hover {
    color: rgba(255, 255, 255, 0.9);
}

.email-verification-flow.dark-mode .shadow-btn {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    box-shadow: none;
    border-radius: 52px;
}

.email-verification-flow.dark-mode .resend-button {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
}

.email-verification-flow.dark-mode .resend-button:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.15);
}

.email-verification-flow.dark-mode .icon {
    filter: brightness(0) invert(1);
}
</style>
